import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../../components/common/SectionHeading';

const Section = styled.section`
  margin-top: 4rem;
  padding: 2.5rem;
  border-radius: 1.5rem;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 768px) {
    padding: 1.75rem;
  }
`;

const RankingList = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RankingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  padding: 1.35rem 1.2rem 1.25rem;
  border-radius: 1.4rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  ${({ rank }) => {
    if (rank === 1) {
      return `
        background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
        border: 2px solid rgba(255, 215, 0, 0.3);
      `;
    } else if (rank === 2) {
      return `
        background: linear-gradient(135deg, rgba(192, 192, 192, 0.1) 0%, rgba(192, 192, 192, 0.05) 100%);
        border: 2px solid rgba(192, 192, 192, 0.3);
      `;
    } else if (rank === 3) {
      return `
        background: linear-gradient(135deg, rgba(205, 127, 50, 0.1) 0%, rgba(205, 127, 50, 0.05) 100%);
        border: 2px solid rgba(205, 127, 50, 0.3);
      `;
    }
    return `
      border: 2px solid transparent;
    `;
  }}
`;

const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 7rem;
  border-radius: 9999px;
  padding: 0.36rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  flex-shrink: 0;
  border: 1px solid ${({ $variant }) => {
    if ($variant === 'position') return 'rgba(29, 78, 216, 0.28)';
    if ($variant === 'word') return 'rgba(4, 120, 87, 0.28)';
    return 'rgba(109, 40, 217, 0.28)';
  }};
  background: ${({ $variant }) => {
    if ($variant === 'position') return '#dbeafe';
    if ($variant === 'word') return '#d1fae5';
    return '#ede9fe';
  }};
  color: ${({ $variant }) => {
    if ($variant === 'position') return '#1e3a8a';
    if ($variant === 'word') return '#065f46';
    return '#6d28d9';
  }};
`;

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  width: 100%;
`;

const PlayerName = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.heading};
  line-height: 1.2;
`;

const TypingScore = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  justify-content: center;
`;

const ScoreNumber = styled.div`
  font-size: 2.1rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.heading};
  letter-spacing: -0.02em;
`;

const ScoreUnit = styled.div`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 600;
`;

const LinkCard = styled(Link)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
    padding: 1.5rem;
    border-radius: 1.4rem;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.default};
    position: relative;
    overflow: hidden;
    min-height: 170px;
    border: 2px solid ${({ theme }) => theme.colors.primary};

    &:hover {
        transform: translateY(-4px);
        box-shadow: ${({ theme }) => theme.shadows.lg};
        background: ${({ theme }) => theme.colors.primary};
        opacity: 0.95;
    }
`;

const LinkIcon = styled.div`
  font-size: 2.6rem;
`;

const LinkText = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.heading};
  text-align: center;
`;

const ArrowIcon = styled.span`
  font-size: 1.5rem;
  margin-left: 0.5rem;
`;

const rankings = [
  { rank: 1, practiceType: 'position', title: '자리연습', badgeText: '자리연습', name: '---', score: '-', unit: '' },
  { rank: 2, practiceType: 'word', title: '낱말연습', badgeText: '낱말연습', name: '---', score: '-', unit: '' },
  { rank: 3, practiceType: 'sentence', title: '단문연습', badgeText: '단문연습', name: '---', score: '-', unit: '' }
];

const RecognitionSection = () => {
  const [rankingData, setRankingData] = useState(rankings);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
        const [positionRes, wordRes, sentenceRes] = await Promise.all([
          fetch(`${backendUrl}/api/ranking?practice_type=position&sort_by=position&period=last_week`),
          fetch(`${backendUrl}/api/ranking?practice_type=word&sort_by=speed&period=last_week`),
          fetch(`${backendUrl}/api/ranking?practice_type=sentence&sort_by=speed&period=last_week`)
        ]);
        if (!positionRes.ok || !wordRes.ok || !sentenceRes.ok) return;

        const [positionData, wordData, sentenceData] = await Promise.all([
          positionRes.json(),
          wordRes.json(),
          sentenceRes.json()
        ]);

        const winners = {
          position: (positionData.rankings || [])[0],
          word: (wordData.rankings || [])[0],
          sentence: (sentenceData.rankings || [])[0]
        };

        setRankingData(rankings.map((item) => {
          const winner = winners[item.practiceType];
          if (!winner) return item;

          const isPosition = item.practiceType === 'position';
          return {
            ...item,
            name: winner.name || '---',
            score: isPosition ? (winner.accuracy || 0) : (winner.typing_speed || 0),
            unit: isPosition ? '%' : '타'
          };
        }));
      } catch (error) {
        // Keep fallback UI when fetch fails.
      }
    };

    fetchRankings();
  }, []);

  return (
    <Section>
      <SectionHeading
        eyebrow="Typing Highlights"
        title="지난주 타자 우수 기록"
        description="연습 유형별 1위 기록을 한눈에 확인해보세요."
      />
      <RankingList>
        {rankingData.map((player) => (
          <RankingCard key={player.rank} rank={player.rank}>
            <TypeBadge $variant={player.practiceType}>{player.badgeText}</TypeBadge>
            <PlayerInfo>
              <PlayerName>{player.name}</PlayerName>
              <TypingScore>
                <ScoreNumber>{player.score}</ScoreNumber>
                <ScoreUnit>{player.unit}</ScoreUnit>
              </TypingScore>
            </PlayerInfo>
          </RankingCard>
        ))}
        <LinkCard to="/ranking">
          <LinkIcon>🎯</LinkIcon>
          <LinkText>
            타자랭킹시스템
            <ArrowIcon>→</ArrowIcon>
          </LinkText>
        </LinkCard>
      </RankingList>
    </Section>
  );
};

export default RecognitionSection;


